<template>
  <aside class="conversation-list">
    <header class="list-header">
      <div>
        <h2>{{ language.t('nav.messages') }}</h2>
        <p>{{ language.t('list.unread', { count: conversations.totalUnread }) }}</p>
      </div>
      <div class="header-actions action-menu-anchor">
        <button class="icon-button" :title="language.t('list.newConversation')" @click.stop="toggleActionMenu">＋</button>
        <div v-if="actionMenuOpen" class="quick-action-menu" @click.stop>
          <button type="button" @click="openAction('addContact')">{{ language.t('list.addContact') }}</button>
          <button type="button" @click="openAction('createGroup')">{{ language.t('list.createGroup') }}</button>
          <button type="button" @click="openAction('joinGroup')">{{ language.t('list.joinGroup') }}</button>
          <button type="button" @click="openAction('broadcast')">{{ language.t('list.broadcast') }}</button>
          <button type="button" @click="openAction('createChannel')">{{ language.t('list.createChannel') }}</button>
        </div>
      </div>
    </header>

    <div class="search-box">
      <input v-model.trim="searchKeyword" :placeholder="language.t('list.searchConversationContact')">
    </div>

    <div class="sidebar-scroll">
      <button
        v-for="item in filteredConversationItems"
        :key="item.resultKey"
        class="conversation-item"
        :class="{ active: isListItemActive(item) }"
        @click="selectListItem(item)"
      >
        <img v-if="item.portrait" class="conversation-avatar image-avatar" :src="item.portrait" alt="">
        <span v-else class="conversation-avatar">{{ item.avatarText }}</span>
        <span class="conversation-copy">
          <strong>{{ item.title }}</strong>
          <small>{{ item.subtitle }}</small>
        </span>
        <span
          v-if="Number(item.unread || 0) > 0"
          class="unread-badge"
          :aria-label="language.t('list.unreadMessages', { count: item.unread })"
        >
          {{ formatUnread(item.unread) }}
        </span>
      </button>

      <div v-if="!filteredConversationItems.length && searchKeyword" class="empty-list">
        <strong>{{ language.t('list.noMatches') }}</strong>
        <span>{{ language.t('list.tryAnotherKeyword') }}</span>
      </div>

      <div v-else-if="!conversations.conversations.length && !conversations.usingDemoData" class="empty-list">
        <strong>{{ language.t('list.noConversations') }}</strong>
        <span>{{ language.t('list.conversationHint') }}</span>
      </div>
    </div>

    <Teleport to="body">
      <div v-if="activeAction" class="modal-backdrop conversation-action-backdrop">
        <form
          class="modal-panel conversation-action-panel"
          :class="{ 'group-create-panel': activeAction === 'createGroup' }"
          @submit.prevent="submitAction"
        >
        <header>
          <strong>{{ actionTitle }}</strong>
          <button type="button" class="icon-button" :title="language.t('common.close')" @click="closeActionModal">×</button>
        </header>

        <template v-if="activeAction === 'addContact'">
          <label>
            <span>{{ language.t('list.userKeyword') }}</span>
            <input v-model.trim="addKeyword" autocomplete="off" :placeholder="language.t('list.userKeywordPlaceholder')">
          </label>
        </template>

        <template v-else-if="activeAction === 'createGroup'">
          <label>
            <span>{{ language.t('list.groupName') }}</span>
            <input v-model.trim="groupName" maxlength="64" autocomplete="off" :placeholder="language.t('list.groupNamePlaceholder')">
          </label>
          <label>
            <span>{{ language.t('list.groupAnnouncement') }}</span>
            <textarea v-model.trim="groupAnnouncement" maxlength="1000" rows="3" :placeholder="language.t('common.optional')"></textarea>
          </label>
          <label>
            <span>{{ language.t('list.groupWelcome') }}</span>
            <textarea v-model.trim="groupWelcomeText" maxlength="1000" rows="3" :placeholder="language.t('list.groupWelcomePlaceholder')"></textarea>
          </label>
          <ContactPicker
            :contacts="filteredContacts"
            :selected-ids="selectedContactIds"
            :keyword="contactKeyword"
            :empty-text="language.t('list.noSelectableContacts')"
            @update:keyword="contactKeyword = $event"
            @toggle="toggleContact"
          />
        </template>

        <template v-else-if="activeAction === 'joinGroup'">
          <label>
            <span>{{ language.t('list.groupKeyword') }}</span>
            <input v-model.trim="joinGroupKey" autocomplete="off" :placeholder="language.t('list.groupKeywordPlaceholder')">
          </label>
        </template>

        <template v-else-if="activeAction === 'broadcast'">
          <label>
            <span>{{ language.t('list.broadcastContent') }}</span>
            <textarea v-model.trim="broadcastText" maxlength="2000" rows="4" :placeholder="language.t('list.broadcastPlaceholder')"></textarea>
          </label>
          <ContactPicker
            :contacts="filteredContacts"
            :selected-ids="selectedContactIds"
            :keyword="contactKeyword"
            :empty-text="language.t('list.noSelectableContacts')"
            @update:keyword="contactKeyword = $event"
            @toggle="toggleContact"
          />
        </template>

        <template v-else-if="activeAction === 'createChannel'">
          <label>
            <span>{{ language.t('list.channelName') }}</span>
            <input v-model.trim="channelName" maxlength="64" autocomplete="off" :placeholder="language.t('list.namePlaceholder')">
          </label>
          <label>
            <span>{{ language.t('list.channelAnnouncement') }}</span>
            <textarea v-model.trim="channelAnnouncement" maxlength="1000" rows="3" :placeholder="language.t('common.optional')"></textarea>
          </label>
          <ContactPicker
            :contacts="filteredContacts"
            :selected-ids="selectedContactIds"
            :keyword="contactKeyword"
            :empty-text="language.t('list.noSelectableContacts')"
            @update:keyword="contactKeyword = $event"
            @toggle="toggleContact"
          />
        </template>

        <p v-if="modalError" class="form-error">{{ language.error(modalError) }}</p>
        <p v-else-if="modalInfo" class="form-info">{{ modalInfo }}</p>

        <div class="modal-actions">
          <button type="button" class="ghost-inline-action" @click="closeActionModal">{{ language.t('common.cancel') }}</button>
          <button class="primary-inline-action" :disabled="actionBusy || submitDisabled">
            {{ actionBusy ? language.t('common.processing') : submitLabel }}
          </button>
        </div>
        </form>
      </div>
    </Teleport>
  </aside>
</template>

<script setup>
import { conversationPreview } from '@/utils/messagePresentation.mjs';
import { computed, defineComponent, h, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useConversationStore } from '@/stores/conversations';
import { useContactStore } from '@/stores/contacts';
import { useLanguageStore } from '@/stores/language';

const conversations = useConversationStore();
const contacts = useContactStore();
const language = useLanguageStore();
const emit = defineEmits(['detail-open']);
const actionMenuOpen = ref(false);
const activeAction = ref('');
const actionBusy = ref(false);
const searchKeyword = ref('');
const addKeyword = ref('');
const groupName = ref('');
const groupAnnouncement = ref('');
const groupWelcomeText = ref('');
const joinGroupKey = ref('');
const broadcastText = ref('');
const channelName = ref('');
const channelAnnouncement = ref('');
const contactKeyword = ref('');
const selectedContactIds = ref([]);

const actionTitles = computed(() => ({
  addContact: language.t('list.addContact'),
  createGroup: language.t('list.createGroup'),
  joinGroup: language.t('list.joinGroup'),
  broadcast: language.t('list.broadcast'),
  createChannel: language.t('list.createChannel')
}));

const submitLabels = computed(() => ({
  addContact: language.t('common.add'),
  createGroup: language.t('common.create'),
  joinGroup: language.t('common.join'),
  broadcast: language.t('common.send'),
  createChannel: language.t('common.create')
}));

const actionTitle = computed(() => actionTitles.value[activeAction.value] || '');
const addCompleted = ref(null);
watch(addKeyword,()=>{addCompleted.value=null;});
const submitLabel = computed(() => activeAction.value==='addContact'&&addCompleted.value ? language.t(addCompleted.value.pending?'contacts.requestSent':'contacts.added') : submitLabels.value[activeAction.value] || language.t('common.confirm'));
const modalError = computed(() => contacts.actionError || conversations.actionError);
const modalInfo = computed(() => contacts.actionInfo || conversations.actionNotice);
const normalizedSearchKeyword = computed(() => searchKeyword.value.trim().toLowerCase());
const contactsByUid = computed(() => {
  const map = new Map();
  contacts.contacts.forEach(contact => {
    const uid = String(contact.uid || contact.id || '');
    if (uid) {
      map.set(uid, contact);
    }
  });
  return map;
});
const filteredConversationItems = computed(() => {
  const keyword = normalizedSearchKeyword.value;
  if (!keyword) {
    return conversations.conversations.map(item => conversationResult(item));
  }

  const conversationResults = conversations.conversations
    .filter(item => matchesConversation(item, keyword, contactForConversation(item)))
    .map(item => conversationResult(item));
  const conversationTargets = new Set(conversations.conversations
    .filter(item => item.type === 'single')
    .map(item => conversationTarget(item)));
  const contactResults = contacts.contacts
    .filter(item => !conversationTargets.has(String(item.uid || item.id || '')))
    .filter(item => matchesContact(item, keyword))
    .map(item => contactResult(item));

  return [...conversationResults, ...contactResults];
});
const filteredContacts = computed(() => {
  const keyword = contactKeyword.value.toLowerCase();
  if (!keyword) {
    return contacts.contacts;
  }
  return contacts.contacts.filter(item => {
    return String(item.title || '').toLowerCase().includes(keyword)
      || String(item.subtitle || '').toLowerCase().includes(keyword)
      || String(item.uid || '').toLowerCase().includes(keyword);
  });
});
const submitDisabled = computed(() => {
  if (activeAction.value === 'addContact') {
    return !addKeyword.value || Boolean(addCompleted.value);
  }
  if (activeAction.value === 'createGroup') {
    return !groupName.value || !selectedContactIds.value.length;
  }
  if (activeAction.value === 'joinGroup') {
    return !joinGroupKey.value;
  }
  if (activeAction.value === 'broadcast') {
    return !broadcastText.value || !selectedContactIds.value.length;
  }
  if (activeAction.value === 'createChannel') {
    return !channelName.value;
  }
  return true;
});

onMounted(() => {
  contacts.refreshContacts().catch(() => {});
});

const ContactPicker = defineComponent({
  name: 'ContactPicker',
  props: {
    contacts: {
      type: Array,
      default: () => []
    },
    selectedIds: {
      type: Array,
      default: () => []
    },
    keyword: {
      type: String,
      default: ''
    },
    emptyText: {
      type: String,
      default: '暂无联系人'
    }
  },
  emits: ['toggle', 'update:keyword'],
  setup(props, { emit }) {
    const pickerLanguage = useLanguageStore();
    return () => h('div', { class: 'contact-picker' }, [
      h('input', {
        value: props.keyword,
        placeholder: pickerLanguage.t('contacts.search'),
        onInput: event => emit('update:keyword', event.target.value)
      }),
      h('div', { class: 'contact-picker-list' }, props.contacts.length
        ? props.contacts.map(contact => h('button', {
          key: contact.uid,
          type: 'button',
          class: ['contact-picker-row', { selected: props.selectedIds.includes(contact.uid) }],
          onClick: () => emit('toggle', contact.uid)
        }, [
          h('span', { class: 'contact-check' }, props.selectedIds.includes(contact.uid) ? '✓' : ''),
          contact.portrait
            ? h('img', { class: 'conversation-avatar image-avatar', src: contact.portrait, alt: '' })
            : h('span', { class: 'conversation-avatar' }, contact.avatarText),
          h('span', { class: 'contact-picker-copy' }, [
            h('strong', contact.title),
            h('small', contact.subtitle)
          ])
        ]))
        : [h('div', { class: 'contact-picker-empty' }, props.emptyText)])
    ]);
  }
});

function searchableValues(...values) {
  return values
    .flatMap(value => {
      if (Array.isArray(value)) {
        return value;
      }
      if (value && typeof value === 'object') {
        return Object.values(value);
      }
      return [value];
    })
    .map(value => String(value || '').toLowerCase())
    .filter(Boolean);
}

function matchesKeyword(values, keyword) {
  const compactKeyword = keyword.replace(/[^\p{L}\p{N}]+/gu, '');
  return searchableValues(values).some(value => {
    if (value.includes(keyword)) {
      return true;
    }
    if (!compactKeyword) {
      return false;
    }
    return value.replace(/[^\p{L}\p{N}]+/gu, '').includes(compactKeyword);
  });
}

function conversationTarget(item) {
  if (item.type !== 'single') {
    return String(item.target || item.raw?.target || item.id || '');
  }
  return String(item.target || item.raw?.target || item.id.replace(/^single-/, '') || '');
}

function contactForConversation(item) {
  const target = conversationTarget(item);
  return target ? contactsByUid.value.get(target) || null : null;
}

function matchesConversation(item, keyword, contact = null) {
  return matchesKeyword([
    item.title,
    conversationPreview(item,language.t),
    item.target,
    item.id,
    item.type,
    item.raw?.target,
    item.raw?.groupId,
    item.raw?.group_id,
    item.raw?.owner_uid,
    item.raw?.name,
    item.raw?.displayName,
    item.raw?.mobile,
    item.raw?.friendAlias,
    item.raw?.alias,
    contact?.title,
    contact?.subtitle,
    contact?.uid,
    contact?.id,
    contact?.alias,
    contact?.address,
    contact?.rawUser,
    contact?.rawFriend
  ], keyword);
}

function matchesContact(item, keyword) {
  return matchesKeyword([
    item.title,
    conversationPreview(item,language.t),
    item.uid,
    item.id,
    item.alias,
    item.address,
    item.rawUser,
    item.rawFriend
  ], keyword);
}

function conversationResult(item) {
  return {
    ...item,
    subtitle: conversationPreview(item,language.t),
    resultKind: 'conversation',
    resultKey: `conversation-${item.id}`
  };
}

function contactResult(contact) {
  return {
    id: contact.id,
    resultKind: 'contact',
    resultKey: `contact-${contact.uid || contact.id}`,
    contact,
    title: contact.title,
    subtitle: contact.subtitle,
    portrait: contact.portrait,
    avatarText: contact.avatarText,
    unread: 0
  };
}

function isListItemActive(item) {
  if (item.resultKind === 'conversation') {
    return item.id === conversations.activeId;
  }
  return `single-${item.contact?.uid || item.id}` === conversations.activeId;
}

function selectListItem(item) {
  if (item.resultKind === 'contact') {
    contacts.openChat(item.contact);
    emit('detail-open');
    return;
  }
  conversations.setActiveConversation(item.id);
  emit('detail-open');
}

function clearStoreMessages() {
  contacts.actionError = '';
  contacts.actionInfo = '';
  conversations.actionError = '';
  conversations.actionNotice = '';
}

function resetActionFields() {
  addKeyword.value = '';
  groupName.value = '';
  groupAnnouncement.value = '';
  groupWelcomeText.value = '';
  joinGroupKey.value = '';
  broadcastText.value = '';
  channelName.value = '';
  channelAnnouncement.value = '';
  contactKeyword.value = '';
  selectedContactIds.value = [];
  clearStoreMessages();
}

function toggleActionMenu() {
  actionMenuOpen.value = !actionMenuOpen.value;
}

function closeActionMenu() {
  actionMenuOpen.value = false;
}

function openAction(action) {
  resetActionFields();
  activeAction.value = action;
  actionMenuOpen.value = false;
  if (['createGroup', 'broadcast', 'createChannel'].includes(action)) {
    contacts.refreshContacts({ force: true });
  }
}

function closeActionModal() {
  activeAction.value = '';
  actionBusy.value = false;
  resetActionFields();
}

function toggleContact(contactId) {
  const selected = new Set(selectedContactIds.value);
  if (selected.has(contactId)) {
    selected.delete(contactId);
  } else {
    selected.add(contactId);
  }
  selectedContactIds.value = Array.from(selected);
}

async function submitAction() {
  if (submitDisabled.value || actionBusy.value) {
    return;
  }
  actionBusy.value = true;
  clearStoreMessages();
  try {
    let ok = false;
    let shouldClose = false;
    if (activeAction.value === 'addContact') {
      const input=addKeyword.value;
      const result = await contacts.addContact(input);
      if(result&&input===addKeyword.value)addCompleted.value=result;
      ok = Boolean(result);
      if (result?.contact) {
        contacts.openChat(result.contact);
      }
    } else if (activeAction.value === 'createGroup') {
      ok = await conversations.createGroup({
        name: groupName.value,
        announcement: groupAnnouncement.value,
        welcomeText: groupWelcomeText.value,
        memberIds: selectedContactIds.value
      });
    } else if (activeAction.value === 'joinGroup') {
      ok = await conversations.joinGroup(joinGroupKey.value);
    } else if (activeAction.value === 'broadcast') {
      ok = await conversations.broadcastMessage(selectedContactIds.value, broadcastText.value);
    } else if (activeAction.value === 'createChannel') {
      ok = await conversations.createGroup({
        name: channelName.value,
        memberIds: selectedContactIds.value,
        announcement: channelAnnouncement.value,
        channel: true
      });
    }
    if (ok && activeAction.value !== 'addContact') {
      shouldClose = true;
    }
    if (shouldClose) {
      window.setTimeout(closeActionModal, 450);
    }
  } finally {
    actionBusy.value = false;
  }
}

document.addEventListener('click', closeActionMenu);
onBeforeUnmount(() => {
  document.removeEventListener('click', closeActionMenu);
});

function formatUnread(value) {
  const count = Number(value || 0);
  return count > 99 ? '99+' : String(count);
}
</script>
